/**
 * Known issues (/bugs): what people reported and an admin published, for everyone to read; a
 * form to report what broke, for anyone signed in, after a sum to answer; and the signed-in
 * person's own reports with where each stands. No email is asked for or shown.
 * The server does the checking (netlify/functions/api/bugs.ts, utils/bugReports.ts).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Bug, Check, Loader2, ShieldCheck, X } from 'lucide-react';
import { BetaTag, BrandMark } from '../components/BrandMark';
import { usePageMeta } from '../hooks/usePageMeta';
import { agoLabel } from '../utils/ideas';
import { BUG_AREAS, MAX_DETAILS, MAX_TITLE, areaLabel, cleanReport, type BugArea, type BugReport, type PublicBugReport } from '../utils/bugReports';
import { bugChallenge, bugErrorText, listBugs, myBugs, reportBug, type Challenge } from '../utils/bugClient';
import type { UserProfile } from '../types';

interface BugsPageProps {
  user: UserProfile | null;
  isAdmin: boolean;
  onBack: () => void;
  /** Signed out: where to go to sign in (the front page) */
  onSignIn: () => void;
  onReview: () => void;
}

const STATUS_LABEL: Record<BugReport['status'], string> = { pending: 'Under review', published: 'Published', rejected: 'Not published' };
const STATUS_TONE: Record<BugReport['status'], string> = { pending: 'bg-lemon-soft text-lemon-deep', published: 'bg-mint-soft text-mint-deep', rejected: 'bg-cream-200 text-ink-soft' };

const ReportCard: React.FC<{ report: PublicBugReport; mine?: boolean; now: number }> = ({ report, mine = false, now }) => (
  <article className="ns-tile flex flex-col gap-2" data-bug-report={report.id} data-bug-status={report.status}>
    <div className="flex items-start justify-between gap-3">
      <h3 className="text-[1rem] font-bold leading-snug">{report.title}</h3>
      {mine ? (
        <span className={`ns-chip ns-chip-sm shrink-0 ${STATUS_TONE[report.status]}`}>{STATUS_LABEL[report.status]}</span>
      ) : report.fixed ? (
        <span className="ns-chip ns-chip-sm shrink-0 bg-mint-soft text-mint-deep"><Check size={12} aria-hidden="true" />Fixed</span>
      ) : (
        <span className="ns-chip ns-chip-sm shrink-0 bg-lemon-soft text-lemon-deep">Open</span>
      )}
    </div>
    <p className="text-[0.9375rem] leading-[1.55] text-ink whitespace-pre-wrap">{report.details}</p>
    <p className="ns-t-meta flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="ns-chip ns-chip-sm bg-cream-200 text-ink-soft">{areaLabel(report.area)}</span>
      <span>Reported by {report.reporterName} · {agoLabel(report.createdAt, now)}</span>
    </p>
    {report.note && (
      <p className="ns-note text-sm" data-tone={report.fixed ? 'ok' : 'brand'} data-bug-note>{report.note}</p>
    )}
  </article>
);

export const BugsPage: React.FC<BugsPageProps> = ({ user, isAdmin, onBack, onSignIn, onReview }) => {
  usePageMeta({ title: 'Known issues — Noodle Studio', description: 'What people reported and what has been fixed. Report what broke; every report is read.', canonical: 'https://noodlestudio.ai/bugs' });
  const [reports, setReports] = useState<PublicBugReport[] | null>(null);
  const [mine, setMine] = useState<BugReport[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [area, setArea] = useState<BugArea>('canvas');
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [answer, setAnswer] = useState('');
  const [sending, setSending] = useState(false);
  const [formProblem, setFormProblem] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const now = Date.now();

  const load = useCallback(async () => {
    try {
      setReports(await listBugs());
      setProblem(null);
    } catch (e) {
      setProblem(bugErrorText(e, 'The reports could not be loaded.'));
      setReports([]);
    }
    if (user) {
      try { setMine(await myBugs()); } catch { /* their own list is a courtesy; the page stands without it */ }
    }
  }, [user]);
  useEffect(() => { void load(); }, [load]);

  const freshChallenge = async () => {
    setAnswer('');
    try { setChallenge(await bugChallenge()); } catch (e) { setFormProblem(bugErrorText(e, 'The human check could not be fetched.')); }
  };
  const startWriting = () => {
    if (!user) { onSignIn(); return; }
    setWriting(true);
    setSent(false);
    setFormProblem(null);
    void freshChallenge();
  };
  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = cleanReport({ title, details, area });
    if (!clean.ok) { setFormProblem(clean.error); return; }
    if (!challenge) { setFormProblem('The human check is still on its way.'); return; }
    if (!answer.trim()) { setFormProblem('Answer the sum first.'); return; }
    setSending(true);
    setFormProblem(null);
    try {
      const { report } = await reportBug({ ...clean, token: challenge.token, answer: answer.trim(), name: user?.name });
      setMine(current => [report, ...current]);
      setTitle(''); setDetails(''); setArea('canvas'); setChallenge(null); setAnswer('');
      setWriting(false);
      setSent(true);
    } catch (err) {
      setFormProblem(bugErrorText(err, 'The report could not be sent.'));
      // A stale or wrong sum: a new one
      void freshChallenge();
    } finally {
      setSending(false);
    }
  };

  const open = (reports ?? []).filter(r => !r.fixed);
  const fixed = (reports ?? []).filter(r => r.fixed);

  return (
    <div className="min-h-screen bg-cream text-ink" data-bugs-page>
      <header className="ns-pagebar">
        <div className="w-full max-w-4xl mx-auto flex items-center justify-between gap-3">
          <button onClick={onBack} className="ns-btn ns-btn-ghost ns-btn-sm shrink-0">
            <ArrowLeft size={14} aria-hidden="true" />
            Back
          </button>
          <Link to="/" className="flex items-center gap-2.5 rounded-full shrink-0" aria-label="Noodle Studio home">
            <BrandMark size={32} />
            <span className="text-[1.0625rem] font-bold tracking-[-0.02em] text-ink">Noodle Studio</span>
            <BetaTag />
          </Link>
        </div>
      </header>

      <main className="px-4 sm:px-6 py-8">
        <div className="max-w-4xl mx-auto flex flex-col gap-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
            <div>
              <p className="text-2xs font-bold tracking-[0.14em] uppercase text-ink-soft">Beta, said plainly</p>
              <h1 className="font-brand font-normal text-[2.5rem] sm:text-[3rem] leading-[1.04] tracking-[-0.01em] mt-2">Known issues</h1>
              <p className="mt-3 text-[1.0625rem] leading-[1.55] text-ink-soft max-w-[34rem]">Every report is read. Once it is reviewed, it shows here for everyone, with a word on what we did. No email, just a sum to prove you are a person.</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {isAdmin && <button type="button" onClick={onReview} className="ns-btn ns-btn-ghost" data-bugs-review><ShieldCheck size={16} aria-hidden="true" />Review reports</button>}
              <button type="button" onClick={startWriting} className="ns-btn ns-btn-primary" data-bugs-report>
                <Bug size={16} aria-hidden="true" />
                {user ? 'Report a bug' : 'Sign in to report'}
              </button>
            </div>
          </div>

          {sent && (
            <p className="ns-note" data-tone="ok" role="status" data-bugs-sent>
              <Check size={16} aria-hidden="true" />
              <span>Thank you. It is under review, and shows here for everyone once it is published.</span>
            </p>
          )}

          {writing && (
            <form onSubmit={send} className="ns-tile flex flex-col gap-4" aria-label="Report a bug" data-bugs-form>
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-[1.125rem] font-bold">What broke?</h2>
                <button type="button" onClick={() => setWriting(false)} className="ns-icon-btn ns-icon-btn-sm" aria-label="Close"><X /></button>
              </div>
              <label className="flex flex-col gap-1.5">
                <span className="ns-t-label">In a few words</span>
                <input type="text" value={title} onChange={e => setTitle(e.target.value)} maxLength={MAX_TITLE} className="ns-input" placeholder="The pager loses the page after I close the script" required />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="ns-t-label">Where</span>
                <select value={area} onChange={e => setArea(e.target.value as BugArea)} className="ns-field">
                  {BUG_AREAS.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="ns-t-label">What happened, and what you expected</span>
                <textarea value={details} onChange={e => setDetails(e.target.value)} maxLength={MAX_DETAILS} rows={5} className="ns-input resize-y" placeholder="What you did, what you saw, what should have happened. A screen or a browser name helps." required />
                <span className="ns-t-meta text-right">{details.length} / {MAX_DETAILS}</span>
              </label>
              <label className="flex flex-col gap-1.5" data-bugs-check>
                <span className="ns-t-label">One sum, to show you are a person</span>
                <span className="text-sm text-ink" data-bugs-question>{challenge?.question ?? 'Fetching the sum…'}</span>
                <input type="text" inputMode="numeric" value={answer} onChange={e => setAnswer(e.target.value)} className="ns-input max-w-[12rem]" placeholder="Your answer" aria-label="The sum's answer" required />
              </label>
              {formProblem && <p className="ns-note" data-tone="danger" role="alert">{formProblem}</p>}
              <div className="flex items-center gap-2">
                <button type="submit" disabled={sending} className="ns-btn ns-btn-primary" data-bugs-send>
                  {sending ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <Bug size={16} aria-hidden="true" />}
                  {sending ? 'Sending' : 'Send the report'}
                </button>
                <button type="button" onClick={() => setWriting(false)} className="ns-btn ns-btn-ghost">Cancel</button>
                <span className="ns-t-meta ml-auto">Up to five reports a day.</span>
              </div>
            </form>
          )}

          {user && mine.length > 0 && (
            <section aria-labelledby="mine-title" className="flex flex-col gap-3" data-bugs-mine>
              <h2 id="mine-title" className="text-2xs font-bold tracking-[0.14em] uppercase text-ink-soft">Your reports</h2>
              {mine.map(report => <ReportCard key={report.id} report={report} mine now={now} />)}
            </section>
          )}

          {problem && <p className="ns-note" data-tone="danger" role="alert">{problem}</p>}

          <section aria-labelledby="open-title" className="flex flex-col gap-3" data-bugs-open>
            <h2 id="open-title" className="text-2xs font-bold tracking-[0.14em] uppercase text-ink-soft">Open · {open.length}</h2>
            {reports === null ? (
              <p className="ns-t-meta flex items-center gap-2"><Loader2 size={14} className="animate-spin" aria-hidden="true" />Loading</p>
            ) : open.length === 0 ? (
              <p className="ns-tile text-[0.9375rem] text-ink-soft">Nothing open right now. If something broke for you, say so above.</p>
            ) : open.map(report => <ReportCard key={report.id} report={report} now={now} />)}
          </section>

          {fixed.length > 0 && (
            <section aria-labelledby="fixed-title" className="flex flex-col gap-3" data-bugs-fixed>
              <h2 id="fixed-title" className="text-2xs font-bold tracking-[0.14em] uppercase text-ink-soft">Fixed · {fixed.length}</h2>
              {fixed.map(report => <ReportCard key={report.id} report={report} now={now} />)}
            </section>
          )}
        </div>
      </main>
    </div>
  );
};

export default BugsPage;
